import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { FaEye, FaEyeSlash } from "react-icons/fa";

import { closeModal } from "../redux/features/modalSlice";
import { changeAdminPassword } from "../redux/services/authService/authApi";
import { successToast, errorToast } from "../Utils/toast";

const ChangePasswordModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [showPassword, setShowPassword] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const [errors, setErrors] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const togglePassword = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => {
      const updatedErrors = { ...prev };

      if (name === "oldPassword" && value.trim()) {
        updatedErrors.oldPassword = "";
      }

      if (name === "newPassword") {
        if (value.trim().length >= 6) {
          updatedErrors.newPassword = "";
        }
      }

      if (name === "confirmPassword") {
        if (value.trim() && value === formData.newPassword) {
          updatedErrors.confirmPassword = "";
        }
      }

      return updatedErrors;
    });
  };

  const handleClose = () => {
    if (!loading) {
      dispatch(closeModal());
    }
  };

  const handleSubmit = async () => {
    const { oldPassword, newPassword, confirmPassword } = formData;

    let newErrors = {};

    if (!oldPassword.trim()) {
      newErrors.oldPassword = "Old password is required";
    }

    if (!newPassword.trim()) {
      newErrors.newPassword = "New password is required";
    } else if (newPassword.length < 6) {
      newErrors.newPassword =
        "New password must be at least 6 characters";
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword =
        "Confirm password is required";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    try {
      setLoading(true);
      const payload = {
        old_password: formData.oldPassword,
        new_password: formData.newPassword,
        confirm_password: formData.confirmPassword,
      };

      const response = await changeAdminPassword(payload);
      if (response?.success) {
        successToast(
          response?.message || "Password changed successfully"
        );
        // Cookies.remove("adminToken");
        localStorage.removeItem("adminToken");
        localStorage.clear();
        sessionStorage.clear();
        dispatch(closeModal());
        navigate("/login", {
          replace: true,
        });
        return;
      }

      errorToast(
        response?.message || "Failed to change password"
      );
    } catch (error) {
      console.log(error);

      errorToast(
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="change-password-modal">
      <div className="change-password-content">

        {/* Old Password */}
        <div className="mb-3">
          <label className="form-label fw-semibold">
            Old Password <span className="text-danger">*</span>
          </label>

          <div className="password-field">
            <input
              type={
                showPassword.oldPassword ? "text" : "password"
              }
              className={`form-control customform ${errors.oldPassword ? "is-invalid" : ""
                }`}
              name="oldPassword"
              value={formData.oldPassword}
              onChange={handleChange}
              placeholder="Enter old password"
            />

            {formData.oldPassword && (
              <span
                className="password-toggle-icon"
                onClick={() =>
                  togglePassword("oldPassword")
                }
              >
                {showPassword.oldPassword ? (
                  <FaEyeSlash />
                ) : (
                  <FaEye />
                )}
              </span>
            )}
          </div>

          {errors.oldPassword && (
            <small className="text-danger">
              {errors.oldPassword}
            </small>
          )}
        </div>

        {/* New Password */}
        <div className="mb-3">
          <label className="form-label fw-semibold">
            New Password <span className="text-danger">*</span>
          </label>

          <div className="password-field">
            <input
              type={
                showPassword.newPassword ? "text" : "password"
              }
              className={`form-control customform ${errors.newPassword ? "is-invalid" : ""
                }`}
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              placeholder="Enter new password"
            />

            {formData.newPassword && (
              <span
                className="password-toggle-icon"
                onClick={() =>
                  togglePassword("newPassword")
                }
              >
                {showPassword.newPassword ? (
                  <FaEyeSlash />
                ) : (
                  <FaEye />
                )}
              </span>
            )}
          </div>

          {errors.newPassword && (
            <small className="text-danger">
              {errors.newPassword}
            </small>
          )}
        </div>

        {/* Confirm Password */}
        <div className="mb-3">
          <label className="form-label fw-semibold">
            Confirm Password{" "}
            <span className="text-danger">*</span>
          </label>

          <div className="password-field">
            <input
              type={
                showPassword.confirmPassword
                  ? "text"
                  : "password"
              }
              className={`form-control customform ${errors.confirmPassword ? "is-invalid" : ""
                }`}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm new password"
            />

            {formData.confirmPassword && (
              <span
                className="password-toggle-icon"
                onClick={() =>
                  togglePassword("confirmPassword")
                }
              >
                {showPassword.confirmPassword ? (
                  <FaEyeSlash />
                ) : (
                  <FaEye />
                )}
              </span>
            )}
          </div>

          {errors.confirmPassword && (
            <small className="text-danger">
              {errors.confirmPassword}
            </small>
          )}
        </div>

        <div className="d-flex justify-content-end gap-2 mt-4">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                />
                Updating...
              </>
            ) : (
              "Change Password"
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default ChangePasswordModal;